import { _isNil, _notNil } from '@/littledash';
import { useEffect } from 'react';
import type {
  DataTableCellIndexCoordinate,
  DataTableCellSelectionChangeEvent,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from './useCellCoordinate';
import { useCellData } from './useCellData';

/**
 * Configuration for auto-insert on cell activation behavior
 *
 * @property coordinate - Cell position in the data table (index-based)
 * @property getValue - Function that returns the value to insert (e.g., DateUtils.dateTimeNow() or '-1')
 */
interface UseAutoInsertOnActivateConfig {
  readonly coordinate: DataTableCellIndexCoordinate;
  readonly getValue: () => string;
}

/**
 * Hook for auto-inserting value on cell activation
 *
 * Provides automatic value insertion when the user activates an empty cell (typically by pressing Enter).
 * Navigates to the next cell after insertion (respects workflow exit columns).
 *
 * ## Features
 *
 * - **Auto-insert on activate**: Activating empty cell inserts value and navigates
 * - **Direction support**: Shift+Enter navigates up, Enter navigates down
 * - **Workflow-aware**: Respects workflow exit columns (no auto-navigation)
 * - **Event cleanup**: Uses AbortController for proper listener cleanup
 *
 * ## Usage
 *
 * @example
 * // Timestamp cell - insert current datetime
 * useAutoInsertOnActivate({
 *   coordinate,
 *   getValue: () => DateUtils.dateTimeNow(),
 * });
 *
 * @example
 * // Collect cell - insert placeholder for API calculation
 * useAutoInsertOnActivate({
 *   coordinate,
 *   getValue: () => '-1',
 * });
 *
 * @param config - Configuration object with coordinate and value generator
 */
export const useAutoInsertOnActivate = (config: UseAutoInsertOnActivateConfig): void => {
  const { coordinate, getValue } = config;
  const { dataTableService } = useDataTable();
  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);

  useEffect(() => {
    if (_notNil(idCoordinate) && _isNil(cellData?.value) && !dataTableService.readonly) {
      const abortController = new AbortController();

      const cellSelectionChangeListener = (event: DataTableCellSelectionChangeEvent) => {
        const { from } = event.detail.selection;
        const isThisCell = from.column === coordinate.column && from.row === coordinate.row;
        const isActivationWithNoContent = event.detail.activate && _isNil(event.detail.content);

        if (!isThisCell || !isActivationWithNoContent) {
          return;
        }

        window.requestAnimationFrame(() => {
          dataTableService
            .updateCells([
              {
                column_id: idCoordinate.column,
                row_id: idCoordinate.row,
                value: getValue(),
              },
            ])
            .then(() => dataTableService.validate());

          // Navigate after insertion (unless in workflow exit column)
          if (!dataTableService.workflowService.workflowActionExit(idCoordinate.column)) {
            dataTableService.moveSelection(event.detail.options?.direction ?? 'DOWN');
          }
        });
      };

      dataTableService.subscribe(
        DataTableEvent.CellSelectionChange,
        cellSelectionChangeListener,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    }
  }, [coordinate, idCoordinate, cellData?.value, dataTableService, getValue]);
};
